import React, { useState, useMemo } from "react";
import { modelToMarkdown } from "@/lib/docModel";
import DocumentView from "@/components/DocumentView";
import { downloadPdf } from "@/lib/pdf";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Download, Copy, X, Share2, Link2, AlertTriangle, Loader2, History, CheckCircle2, MessageSquareWarning, Send } from "lucide-react";

const EVENT_LABEL = { published: "Published for review", approved: "Approved by client", changes_requested: "Changes requested" };
const EVENT_ICON = { published: Send, approved: CheckCircle2, changes_requested: MessageSquareWarning };

function ModalActions({ publishable, publishing, onPublish, onCopyMarkdown, downloading, onDownloadPdf, onClose }) {
  return (
    <div className="flex items-center gap-2">
      {publishable && (
        <button data-testid="publish-review-btn" onClick={onPublish} disabled={publishing} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-slate-800 hover:bg-slate-700 transition-colors disabled:opacity-60">
          {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Publish for Review
        </button>
      )}
      <button data-testid="copy-markdown-btn" onClick={onCopyMarkdown} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-slate-800 hover:bg-slate-700 transition-colors">
        <Copy className="h-4 w-4" /> Copy Markdown
      </button>
      <button data-testid="download-pdf-btn" onClick={onDownloadPdf} disabled={downloading} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-emerald-500 hover:bg-emerald-600 transition-colors disabled:opacity-60">
        {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
      </button>
      <button data-testid="close-modal-btn" onClick={onClose} className="ml-1 h-9 w-9 flex items-center justify-center rounded-md hover:bg-slate-800 transition-colors">
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

function AuditTrail({ events }) {
  if (!events.length) return null;
  return (
    <div data-testid="audit-trail" className="px-6 pb-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">
        <History className="h-3.5 w-3.5" /> Review History
      </div>
      <ol className="space-y-1">
        {events.slice().reverse().map((ev, i) => {
          const Icon = EVENT_ICON[ev.type] || Send;
          const iconColor = ev.type === "approved" ? "text-emerald-400" : ev.type === "changes_requested" ? "text-amber-400" : "text-slate-400";
          return (
            <li key={`${ev.type}-${ev.at}-${i}`} className="flex items-center gap-2 text-xs text-slate-300" data-testid={`audit-event-${ev.type}`}>
              <Icon className={`h-3.5 w-3.5 ${iconColor}`} />
              <span className="font-medium">{EVENT_LABEL[ev.type] || ev.type}</span>
              <span className="text-slate-500">· {new Date(ev.at).toLocaleString()}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ReviewMeta({ shareUrl, review, events, onCopyLink }) {
  return (
    <>
      {shareUrl && (
        <div className="px-6 pb-3 flex items-center gap-2 text-sm">
          <Link2 className="h-4 w-4 text-emerald-400" />
          <span className="text-slate-400">Client review link:</span>
          <code data-testid="share-url" className="bg-slate-800 px-2 py-1 rounded text-emerald-300 truncate max-w-md">{shareUrl}</code>
          <button data-testid="copy-link-btn" onClick={onCopyLink} className="text-slate-300 hover:text-white">Copy</button>
        </div>
      )}
      {review?.status === "Revision Needed" && review?.feedback && (
        <div className="px-6 pb-3 flex items-start gap-2 text-sm text-amber-200">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span><span className="font-semibold">Client feedback:</span> {review.feedback}</span>
        </div>
      )}
      <AuditTrail events={events} />
    </>
  );
}

export default function DocumentModal({ model, docType, projectId, brandColor = "#10B981", agencyName = "Performance Flow Pro", review, onPublished, onClose, publishable = true }) {
  const markdown = useMemo(() => modelToMarkdown(model), [model]);
  const [publishing, setPublishing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [shareSlug, setShareSlug] = useState(review?.slug || null);
  const [events, setEvents] = useState(review?.events || []);
  const status = review?.status || (shareSlug ? "In Review" : "Draft");

  const shareUrl = shareSlug ? `${window.location.origin}/review/${shareSlug}` : null;

  const copyMarkdown = async () => {
    try { await navigator.clipboard.writeText(markdown); toast.success("Markdown copied to clipboard"); }
    catch (e) { toast.error("Copy failed — clipboard not available"); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Share link copied"); }
    catch (e) { toast.error("Copy failed"); }
  };
  const handleDownloadPdf = async () => {
    setDownloading(true);
    try { await downloadPdf({ model, brandColor, agencyName, filename: model.title }); toast.success("PDF downloaded"); }
    catch (e) { toast.error("PDF download failed"); }
    finally { setDownloading(false); }
  };
  const publishForReview = async () => {
    setPublishing(true);
    try {
      const res = await api.post(`/projects/${projectId}/reviews/${docType}/publish`);
      setShareSlug(res.data.slug);
      setEvents(res.data.events || [...events, { type: "published", at: new Date().toISOString() }]);
      onPublished?.(docType, { status: "In Review", slug: res.data.slug });
      toast.success("Published for client review");
    } catch (e) {
      toast.error("Failed to publish");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/85 backdrop-blur-sm flex flex-col" data-testid="document-modal">
      <div className="no-print bg-slate-900 text-white border-b border-slate-800 flex-shrink-0">
        <div className="h-16 flex items-center justify-between px-6">
          <div className="min-w-0 flex items-center gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-400 font-semibold">Document Preview</p>
              <h2 className="font-display font-semibold truncate">{model.title}</h2>
            </div>
            <StatusBadge status={status} />
          </div>
          <ModalActions
            publishable={publishable}
            publishing={publishing}
            onPublish={publishForReview}
            onCopyMarkdown={copyMarkdown}
            downloading={downloading}
            onDownloadPdf={handleDownloadPdf}
            onClose={onClose}
          />
        </div>
        <ReviewMeta shareUrl={shareUrl} review={review} events={events} onCopyLink={copyLink} />
      </div>

      <div className="flex-1 overflow-y-auto py-8 px-4 flex justify-center">
        <DocumentView model={model} brandColor={brandColor} agencyName={agencyName} />
      </div>
    </div>
  );
}
